import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Check, Search, X } from "lucide-react";
import {
	Text,
	Input,
	ColorV2,
	Label,
	Button,
} from "@adoptaunabuelo/react-components";
import { useNavigate } from "react-router-dom";
import { User, UserScheme } from "client-v2";
import moment from "moment";
import TableGenericApp from "../tables/TableGenericApp";
import { ChevronRight, Pencil, Plus, Mail } from "lucide-react";
import { Player } from "@lottiefiles/react-lottie-player";
import animation from "@assets/animations/button-loading.json";
import { useSelector } from "react-redux";
import { State } from "@redux/index";

const MainContainer = styled.div`
	background-color: ${ColorV2.surface.invert};
	min-width: auto;
	max-height: calc(100vh - 56px);
	overflow: auto;
	@media (min-width: 768px) {
		border-radius: 12px;
		max-height: calc(100vh - 72px - 24px - 42px);
	}
`;

const ScrollableContainer = styled.div<{ $isSearchFocused: boolean }>``;

const HeaderContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 8px 16px;
	@media (min-width: 768px) {
		padding: 12px 16px;
		justify-content: flex-start;
		flex-direction: row;
		align-items: center;
	}
`;
const FixedButtonView = styled.div`
	display: flex;
	flex-direction: row;
	flex: 1;
	justify-content: flex-end;
	gap: 8px;
	@media (max-width: 768px) {
		flex-direction: column;
		position: absolute;
		bottom: 16px;
		right: 16px;
		z-index: 100;
	}
`;

const FixedButton = styled(Button)<{ $edit?: boolean }>`
	@media (max-width: 768px) {
		height: 52px !important;
		width: 52px !important;
		padding: 0px !important;
		min-width: ${(props) => (props.$edit ? "120px" : "unset")} !important;
	}
`;

const AgeColumnContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;

	@media (min-width: 768px) {
		justify-content: flex-start;
	}
`;
const SearchContainer = styled.div<{ $isSearchFocused: boolean }>`
	width: 100%;

	@media (min-width: 768px) {
		display: flex;
		align-items: center;
		justify-content: flex-end;
	}

	@media (max-width: 767px) {
		display: ${({ $isSearchFocused }) =>
			$isSearchFocused ? "block" : "block"};
	}
`;

const LoadingView = styled.div`
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: rgba(0, 0, 0, 0.7);
	z-index: 1000000;
`;
const CheckView = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 24px;
	width: 24px;
	border-radius: 32px;
	background-color: ${ColorV2.surface.primary};
	@media (max-width: 767px) {
		height: 28px;
		width: 28px;
	}
`;
const GrandpaNameView = styled.div`
	display: flex;
	flex-direction: column;
	min
`;

const Row = styled.div`
	display: flex;
	flex-direction: row;
	gap: 8px;
`;
const Checkbox = styled.input`
	height: 22px;
	width: 22px;
	@media (max-width: 767px) {
		height: 28px;
		width: 28px;
	}
`;

type GrandpaWithLetterCount = UserScheme & {
	letterCount?: number;
};

const GrandpasTable: React.FC<Props> = (props) => {
	const navigate = useNavigate();
	const isDesktop = useSelector((state: State) => state.screen.isDesktop);
	const [sortOrderByName, setSortOrderByName] = useState<"asc" | "desc">(
		"asc"
	);
	const [sortOrderByAge, setSortOrderByAge] = useState<"asc" | "desc" | null>(
		null
	);
	const [searchTerm, setSearchTerm] = useState("");
	const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
	const [edit, setEdit] = useState(false);
	const [grandpas, setGrandpas] = useState<UserScheme[]>([]);
	const [editedGrandpas, setEditedGrandpas] = useState<UserScheme[]>([]);
	const [loading, setLoading] = useState(false);

	const totalGrandpas = props.filterState.letterFilter
		? grandpas.length
		: props.grandpas.length;

	const getAge = (date?: string) =>
		date ? moment().diff(moment(date), "years") : 0;
	useEffect(() => {
		let filteredGrandpas = props.grandpas.filter((grandpa) =>
			`${grandpa.name} ${grandpa.surname}`
				.toLowerCase()
				.includes(searchTerm.toLowerCase())
		);

		if (props.filterState.letterFilter) {
			filteredGrandpas = filteredGrandpas.filter(
				(g: any) => (g.letterCount ?? 0) > 0
			);
		}

		if (sortOrderByName) {
			filteredGrandpas = [...filteredGrandpas].sort((a, b) => {
				const fullNameA = `${a.name} ${a.surname}`.toLowerCase();
				const fullNameB = `${b.name} ${b.surname}`.toLowerCase();
				return sortOrderByName === "asc"
					? fullNameA.localeCompare(fullNameB)
					: fullNameB.localeCompare(fullNameA);
			});
		}

		if (sortOrderByAge) {
			filteredGrandpas = [...filteredGrandpas].sort((a, b) => {
				const ageA = getAge(a.birthday?.iso);
				const ageB = getAge(b.birthday?.iso);
				return sortOrderByAge === "asc" ? ageA - ageB : ageB - ageA;
			});
		}

		setGrandpas(filteredGrandpas);
	}, [
		props.grandpas,
		searchTerm,
		sortOrderByName,
		sortOrderByAge,
		props.filterState.letterFilter,
	]);

	const handleSort = (type: "name" | "age") => {
		if (type === "name") {
			setSortOrderByName((prev) => (prev === "asc" ? "desc" : "asc"));
			setSortOrderByAge(null);
		} else {
			setSortOrderByAge((prev) => (prev === "asc" ? "desc" : "asc"));
			setSortOrderByName("asc");
		}
	};

	const handleFocusChange = (focused: boolean) => {
		setIsSearchFocused(focused);
		props.onSearchFocusChange?.(focused);
		if (!focused) setSearchTerm("");
	};

	const onCheckboxChange = (
		grandpa: UserScheme,
		checked: boolean,
		type: "letter"
	) => {
		const editIndex = grandpas.findIndex(
			(g) => g.objectId === grandpa.objectId
		);
		const updatedGrandpas = grandpas.map((item, index) =>
			editIndex === index
				? {
						...grandpa,
						data: {
							...grandpa.data,
							[type]: checked ? true : false,
						},
				  }
				: item
		);
		setGrandpas(updatedGrandpas);

		const editedIndex = editedGrandpas.findIndex(
			(g) => g.objectId === grandpa.objectId
		);
		if (editedIndex < 0) {
			setEditedGrandpas([
				...editedGrandpas,
				{
					...grandpa,
					data: {
						...grandpa.data,
						[type]: checked ? true : false,
					},
				},
			]);
		} else {
			const temp = [...editedGrandpas];
			temp[editedIndex] = {
				...editedGrandpas[editedIndex],
				data: {
					...editedGrandpas[editedIndex].data,
					[type]: checked ? true : false,
				},
			};
			setEditedGrandpas(temp);
		}
	};

	const onSave = async () => {
		if (edit) {
			setLoading(true);
			await Promise.all(
				editedGrandpas.map((item) => {
					User.set(item.objectId, {
						data: {
							letter: item.data?.letter,
						},
					});
				})
			)
				.then(() => {
					setLoading(false);
					setEdit(!edit);
				})
				.catch((e) => {
					setLoading(false);
				});
		} else setEdit(!edit);
	};

	return (
		<MainContainer>
			{props.loading && (
				<LoadingView>
					<Player
						style={{ height: 150, width: 150 }}
						loop={true}
						autoplay={true}
						src={animation}
					/>
				</LoadingView>
			)}

			<HeaderContainer>
				{(!isSearchFocused || isDesktop) && (
					<>
						<Text
							type={isDesktop ? "h4" : "h1"}
							weight="semibold"
							style={{
								color: ColorV2.text.neutralHard,
								width: isDesktop ? "200px" : undefined,
							}}
						>
							{totalGrandpas} mayores
						</Text>
					</>
				)}

				<SearchContainer $isSearchFocused={isSearchFocused}>
					<Input
						type="text"
						LeftContent={
							<Search
								style={{ marginRight: 8 }}
								size={20}
								color={ColorV2.border.neutralMedium}
							/>
						}
						placeholder="Buscar por nombre"
						design="primary"
						style={{
							borderRadius: "1000px",
							minWidth: 240,
							transition: "width 0.3s ease",
						}}
						onFocus={() => handleFocusChange(true)}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
					{((!isDesktop && isSearchFocused) ||
						(isDesktop && searchTerm.length > 0)) && (
						<Text
							type="b1"
							weight="medium"
							style={{
								cursor: "pointer",
								color: ColorV2.text.neutralHard,
								background: "transparent",
								border: "none",
								padding: 0,
								marginLeft: isDesktop ? "16px" : 0,
								position: isDesktop ? "static" : "relative",
								top: isDesktop ? "auto" : "8px",
								display: "block",
								width: isDesktop ? "auto" : "100%",
								textAlign: isDesktop ? "left" : "center",
							}}
							onClick={() => handleFocusChange(false)}
						>
							Cancelar
						</Text>
					)}

					<FixedButtonView>
						<FixedButton
							size="small"
							$edit={edit}
							style={{
								background: edit
									? ColorV2.surface.primary
									: ColorV2.surface.invert,
								color:
									!edit && isDesktop
										? ColorV2.text.neutralHard
										: ColorV2.text.invert,
								boxShadow: `0px 1px 3px rgba(0, 0, 0, 0.1),
								0px 1px 2px rgba(0, 0, 0, 0.24)`,
							}}
							loading={loading}
							onClick={onSave}
						>
							{!edit && (
								<Pencil
									size={isDesktop ? 20 : 24}
									color={ColorV2.text.neutralHard}
								/>
							)}
							{edit
								? "Guardar"
								: isDesktop
								? "Editar"
								: undefined}
						</FixedButton>
						{!edit && (
							<FixedButton
								size="small"
								onClick={() => navigate("/abuelos/registro")}
							>
								<Plus
									size={isDesktop ? 20 : 24}
									color={ColorV2.text.invert}
								/>
								{isDesktop ? "Añadir" : undefined}
							</FixedButton>
						)}
					</FixedButtonView>
				</SearchContainer>
			</HeaderContainer>
			<ScrollableContainer $isSearchFocused={isSearchFocused}>
				<TableGenericApp
					showAvatar={true}
					data={grandpas}
					onRowClick={(grandpa: UserScheme) => {
						if (!edit) props.onSelectGrandpa(grandpa);
					}}
					filters={
						[
							// Filtro de cartas desactivado temporalmente
							// {
							// 	id: "letterFilter",
							// 	type: "boolean",
							// 	placeholder: "Con cartas",
							// 	selected: props.filterState.letterFilter,
							// 	onChange: (active) =>
							// 		props.setFilterState((prev: any) => ({
							// 			...prev,
							// 			letterFilter: active,
							// 		})),
							// },
						]
					}
					noResultsMessage="No hay abuelos que coincidan con tu búsqueda"
					columns={[
						{
							header: "Nombre",
							key: "Nombre",
							icon: require("../../assets/images/arrow-up.png"),
							onClick: () => handleSort("name"),
							render: (grandpa: GrandpaWithLetterCount) => (
								<GrandpaNameView>
									<Text
										type="p"
										weight="regular"
										style={{
											color: ColorV2.text.neutralHard,
										}}
									>
										{grandpa.name} {grandpa.surname}
									</Text>

									{!isDesktop && !edit && (
										<Row>
											{grandpa.data?.letter && (
												<Label
													icon={
														<Mail
															size={20}
															style={{
																color: ColorV2
																	.surface
																	.greenHard,
															}}
														/>
													}
													text="Cartas"
													color={
														ColorV2.surface
															.greenHard
													}
													backgroundColor={
														ColorV2.surface
															.greenSoft
													}
													style={{
														height: 22,
														padding: "2px 8px",
														borderRadius: 26,
													}}
												/>
											)}
											{(grandpa.letterCount ?? 0) > 0 && (
												<Label
													text={
														grandpa.letterCount?.toString() ||
														"0"
													}
													color={
														grandpa.data?.letter
															? ColorV2.surface
																	.greenHard
															: ColorV2.text
																	.neutralSoft
													}
													backgroundColor={
														grandpa.data?.letter
															? ColorV2.surface
																	.greenSoft
															: ColorV2.surface
																	.neutralSoft
													}
													style={{
														height: 22,
														padding: "2px 8px",
														borderRadius: 1000,
														opacity: grandpa.data
															?.letter
															? 1
															: 0.6,
													}}
												/>
											)}
											{/* Spacer 
											<Label
												text="Voluntariado"
												color={
													grandpa.data?.volunteering
														? "white"
														: ColorV2.text
																.neutralMedium
												}
												backgroundColor={
													grandpa.data?.volunteering
														? ColorV2.surface
																.primary
														: ColorV2.surface
																.neutralSoft
												}
												style={{
													height: 22,
													borderRadius: 26,
													opacity: grandpa.data?.volunteering
														? 1
														: 0.6,
												}}
											/>
											*/}
										</Row>
									)}
								</GrandpaNameView>
							),
						},
						...(isDesktop || edit
							? [
									{
										header: isDesktop
											? "Voluntariado"
											: "Volunt.",
										key: "Voluntariado",
										render: (grandpa: UserScheme) => (
											<CheckView
												style={{
													backgroundColor: grandpa
														.data?.volunteering
														? ColorV2.surface
																.primary
														: ColorV2.surface
																.background,
												}}
											>
												{/* Solo lectura: la residencia no puede modificar el voluntariado (podría romper un match existente) */}
												{grandpa.data?.volunteering ? (
													<Check
														color={"white"}
														width={18}
														height={18}
													/>
												) : (
													<X
														color={
															ColorV2.border
																.neutralSoft
														}
														width={18}
														height={18}
													/>
												)}
											</CheckView>
										),
									},
									{
										header: "Cartas",
										key: "Cartas",
										render: (
											grandpa: GrandpaWithLetterCount
										) => (
											<div
												style={{
													display: "flex",
													flexDirection: "row",
													alignItems: "center",
													gap: 8,
												}}
											>
												<CheckView
													style={{
														backgroundColor:
															"transparent",
													}}
												>
													{edit ? (
														<Checkbox
															type="checkbox"
															checked={
																!!grandpa.data
																	?.letter
															}
															onChange={(e) =>
																onCheckboxChange(
																	grandpa,
																	e.target
																		.checked,
																	"letter"
																)
															}
														/>
													) : (
														grandpa.data
															?.letter && (
															<Mail
																size={20}
																style={{
																	padding: 2,
																	borderRadius: 4,
																	backgroundColor:
																		ColorV2
																			.surface
																			.greenSoft,
																	color: ColorV2
																		.surface
																		.greenHard,
																}}
															/>
														)
													)}
												</CheckView>

												{grandpa.data?.letter &&
													(grandpa.letterCount ?? 0) >
														0 && (
														<Label
															text={
																(
																	grandpa.letterCount ??
																	0
																).toString() ||
																"0"
															}
															color={
																ColorV2.surface
																	.greenHard
															}
															backgroundColor={
																ColorV2.surface
																	.greenSoft
															}
															style={{
																height: 24,
																borderRadius: 4,
															}}
														/>
													)}
											</div>
										),
									},
								]
							: []),
						...(isDesktop || (!isDesktop && !edit)
							? [
									{
										header: "Edad",
										key: "Edad",
										icon: require("../../assets/images/arrow-up.png"),
										onClick: () => handleSort("age"),
										render: (grandpa: UserScheme) => (
											<AgeColumnContainer>
												<Text
													type={
														isDesktop ? "p2" : "c1"
													}
													weight="regular"
													style={{
														color: isDesktop
															? ColorV2.text
																	.neutralHard
															: ColorV2.text
																	.neutralMedium,
													}}
												>
													{getAge(
														grandpa.birthday?.iso
													)}{" "}
													años
												</Text>
												{!isDesktop && (
													<ChevronRight
														size={20}
														color={
															ColorV2.border
																.neutralMedium
														}
													/>
												)}
											</AgeColumnContainer>
										),
									},
								]
							: []),
					]}
				/>
			</ScrollableContainer>
		</MainContainer>
	);
};

interface Props {
	loading?: boolean;
	grandpas: UserScheme[];
	onSelectGrandpa: (grandpa: UserScheme) => void;
	onSearchFocusChange?: (isFocused: boolean) => void;
	filterState: {
		letterFilter: boolean;
	};
	setFilterState: React.Dispatch<React.SetStateAction<any>>;
}

export default GrandpasTable;
